/**
 * What the credits widgets report on their BI events: `itemSeen` (198:6) when the widget comes into
 * view, and `clickCta` (198:10) when it is acted on, each carrying which widget it was.
 *
 * Import-free on purpose, so a unit test can exercise it without dragging the app's graph in (see the
 * Tests section of the package CLAUDE.md).
 *
 * ⚠️ **These literals are a THREE-SURFACE contract.** The same strings are sent by iOS
 * (`SymphonyBI.swift`) and Android (`SymphonyBi.kt`), because a funnel that cannot join web to the
 * phones is not a funnel — it is three separate numbers. Renaming one here silently splits the report;
 * change all three together, exactly as `ENTITY_TYPE` in `use-generic-bi.ts` documents for itself.
 */

/** The `additional_fields` key carrying the widget's identity. */
export const CREDITS_WIDGET_FIELD = 'widget_type';

/**
 * The two widgets the credits funnel measures, spelled as the BI catalog expects them.
 *
 * The space in `almost ooc` is deliberate — it is the value as specified, not a slug, and "correcting"
 * it to `almost_ooc` would make the web rows stop matching the phones' and the dashboard's.
 */
export const CREDITS_WIDGET_TYPE = {
  /**
   * The out-of-credits "earn free credits" offer, at all three of its entry points (the rail/drawer
   * credits card in the red band, a routine paused for credits, the Max+ buy-credits sheet). The
   * `entry_point` field already separates those, so they share one widget type.
   */
  outOfCredits: 'ooc',
  /** The in-chat "running low on credits" card — the `low` variant only; see below. */
  almostOutOfCredits: 'almost ooc',
} as const;

export type CreditsWidgetType = (typeof CREDITS_WIDGET_TYPE)[keyof typeof CREDITS_WIDGET_TYPE];

/**
 * The widget type for an in-chat credits card, or `null` for a card that this funnel does not measure.
 *
 * The card has two variants and only one of them is a credits WARNING: `low` is "running low", which is
 * the `almost ooc` widget. `renewed` is "your credits are back" — a different moment with a different
 * intent, and deliberately outside the two types the funnel defines. Returning null rather than
 * inventing a third value keeps the reported set closed to what the catalog expects; a `renewed` card
 * therefore reports no impression, which is a decision and not an omission.
 */
export function creditsCardWidgetType(variant: string): CreditsWidgetType | null {
  return variant === 'low' ? CREDITS_WIDGET_TYPE.almostOutOfCredits : null;
}
